import { FC } from 'react';
import AddJourneyModal from './AddJourneyModal';
import useManageJourneys from './ManageJourneys.hook';
import {
  manageJourneysDialogStyle,
  headerCellStyles,
  journeyNameStyles,
  statusBadgeStyles,
  noJourneysStyles,
  tableStyles,
  tableRowStyles,
} from './ManageJourneys.style';
import DefaultDialogTitle from 'components/Dialogs/DefaultDialogTitle';
import Box from 'yoda-ui/components/Box';
import Button from 'yoda-ui/components/Button';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import Dialog from 'yoda-ui/components/Dialogs/Dialog';
import Icon, { Icons } from 'yoda-ui/components/Icons/Icon';
import Loader from 'yoda-ui/components/Loader';
import Table from 'yoda-ui/components/Table';
import TableBody from 'yoda-ui/components/Table/TableBody';
import TableCell from 'yoda-ui/components/Table/TableCell';
import TableContainer from 'yoda-ui/components/Table/TableContainer';
import TableHead from 'yoda-ui/components/Table/TableHead';
import TableRow from 'yoda-ui/components/Table/TableRow';
import { YodaSpacing } from 'yoda-ui/yodaTheme';

type ManageJourneysProps = {
  onClose: () => void;
  isOpen: boolean;
};

const ManageJourneys: FC<ManageJourneysProps> = ({ onClose, isOpen = false }) => {
  const {
    journeys,
    journeysLoading,
    openAddJourneyModal,
    setOpenAddJourneyModal,
    handleAddJourney,
    handleJourneySaved,
    formatSendTime,
    t,
  } = useManageJourneys();

  return (
    <Dialog
      open={ isOpen }
      onClose={ onClose }
      maxWidth="md"
      fullWidth
      sx={ manageJourneysDialogStyle }
    >
      <Box padding={ YodaSpacing.xLarge }>
        <DefaultDialogTitle cancelDialog={ onClose }>
          { t('manage_journeys_title') }
        </DefaultDialogTitle>

        <Box paddingTop={ YodaSpacing.small } paddingX={ YodaSpacing.medium }>
          <Box display="flex" justifyContent="flex-end" marginBottom={ YodaSpacing.medium }>
            <Button
              buttonType={ ButtonType.secondary }
              onClick={ handleAddJourney }
            >
              <Icon name={ Icons.add } fontSize="small" />
              { t('manage_journeys_add_button') }
            </Button>
          </Box>

          <AddJourneyModal
            open={ openAddJourneyModal }
            setOpen={ setOpenAddJourneyModal }
            onJourneySave={ handleJourneySaved }
          />

          {
            journeysLoading ? (
              <Box display="flex" justifyContent="center" padding={ YodaSpacing.large }>
                <Loader center />
              </Box>
            ) : (
              <TableContainer>
                <Table sx={ tableStyles }>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={ headerCellStyles }>
                        { t('manage_journeys_column_reminder') }
                      </TableCell>
                      <TableCell sx={ headerCellStyles } align="center">
                        { t('manage_journeys_column_send_time') }
                      </TableCell>
                      <TableCell sx={ headerCellStyles } align="center">
                        { t('manage_journeys_column_status') }
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      journeys && journeys.length > 0 ? (
                        journeys.map((journey) => (
                          <TableRow key={ journey._id } sx={ tableRowStyles }>
                            <TableCell>
                              <Box sx={ journeyNameStyles }>
                                { journey.name }
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              { formatSendTime(journey.timeInHour, journey.format) }
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={ statusBadgeStyles(journey.active) }>
                                { journey.active ? t('status_active') : t('status_inactive') }
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={ 3 } align="center">
                            <Box sx={ noJourneysStyles } padding={ YodaSpacing.medium }>
                              { t('manage_journeys_no_journeys') }
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            )
          }
        </Box>
      </Box>
    </Dialog>
  );
};

export default ManageJourneys;
